/* eslint-disable @khanacademy/ts-no-error-suppressions */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useEffect, useRef, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge} from "@khanacademy/wonder-blocks-typography";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    useCoeditorAPIContext,
    useCoeditorContext,
} from "@khan/guide-coeditor-core";

import pencilIcon from "@phosphor-icons/core/regular/pencil-simple.svg";

type Props = {
    threadId: string;
    navigationId: string;
    toolTitle: string;
    viewOnly?: boolean;
};

export default function AIToolTitle({
    threadId = "",
    navigationId = "",
    toolTitle = "",
    viewOnly = false,
}: Props) {
    const {documentTitle, open, readOnly} = useCoeditorContext();
    const {setDocumentTitle} = useCoeditorAPIContext();

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditingTitle) {
            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorDocumentRenameSelected",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId,
                aiGuideNavigationId: navigationId,
            });
        }
    }, [isEditingTitle, threadId, navigationId]);

    if (!open) {
        return (
            <View style={styles.container}>
                <LabelLarge style={styles.title}>{toolTitle}</LabelLarge>
            </View>
        );
    }

    const displayedText = isEditingTitle
        ? documentTitle
        : documentTitle || toolTitle;

    return (
        <View style={styles.container}>
            <View style={styles.sizer} data-text={displayedText}>
                <TextField
                    readOnly={viewOnly}
                    ref={inputRef}
                    id="ai-tool-title-editor"
                    disabled={!open}
                    value={displayedText}
                    onChange={setDocumentTitle}
                    placeholder={t`Name your document...`}
                    aria-label={t`Edit document title`}
                    style={styles.textField}
                    autoComplete="off"
                    onFocus={() => setIsEditingTitle(true)}
                    onBlur={() => setIsEditingTitle(false)}
                    // @ts-expect-error this is a valid prop!
                    size={displayedText ? "1" : undefined}
                    spellCheck="false"
                />
            </View>
            {!viewOnly && (
                <IconButton
                    icon={pencilIcon}
                    size="small"
                    kind="tertiary"
                    actionType="neutral"
                    style={styles.icon}
                    aria-label={t`Edit document title`}
                    disabled={readOnly}
                    onClick={() => {
                        setIsEditingTitle(true);
                        inputRef.current?.focus();
                    }}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: "row",
        alignItems: "center",
    },
    sizer: {
        display: "grid",
        alignItems: "center",
        "::after": {
            content: "attr(data-text)",
            visibility: "hidden",
            fontWeight: "bold",
            fontSize: 16,
            whiteSpace: "pre-wrap",
            padding: spacing.xSmall_8,
            border: "2px solid transparent",
            gridArea: "1 / 2",
            color: "green",
        },
    },
    title: {
        padding: spacing.xSmall_8,
        border: `1px solid transparent`,
        boxSizing: "border-box",
        gridRow: 1,
        gridColumn: 1,
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "pre",
    },
    textField: {
        fontWeight: "bold",
        background: "none",
        padding: spacing.xSmall_8,
        border: "2px solid transparent",
        gridArea: "1 / 2",
        width: "initial",
        ":focus": {
            border: `2px solid ${color.blue}`,
        },
    },
    icon: {
        marginLeft: spacing.xxxxSmall_2,
    },
});
